/* museum.js — the Gallery page: boot, the chart's chrome, and the wiring.
 *
 * This file used to be 10,658 lines: a three.js chart, a 3D gallery room, a
 * build-detail exhibit, two tuning panels and the field atlas. The room is
 * gone, the exhibit lives on the product pages, and the chart is an image now
 * (see museum-chart.js for why and how). What is left here is the page itself —
 * the splash and its watchdogs, the status line, the phone stepper, the
 * tooltip, and the handful of calls that join the four modules:
 *
 *   museum-data.js    the content, Cody's numbers unchanged
 *   museum-chart.js   the flat chart: FORUM_CHART
 *   museum-atlas.js   the field atlas panel and the film player: FORUM_ATLAS
 *   museum-hang.js    the photo caches, hung on a wall: FORUM_HANG
 *   museum-season.js  a Dead Warrior Society season: FORUM_SEASON (K22)
 *
 * Everything in here is event-driven. There is no animation frame on this page,
 * which is the whole point of the round.
 */
(function () {
  'use strict';

  if (window.FORUM_MUSEUM && window.FORUM_MUSEUM.mounted) return;

  var D = window.MUSEUM_DATA;
  function $(id) { return document.getElementById(id); }
  function quietNow() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  var quiet = quietNow();

  /* ====================================================================
     THE STATUS LINE
     A keyed map, so the newest thing to speak is the thing on screen, and
     a slow film cannot be silenced by a finished chart. Published on
     FORUM_MUSEUM because the atlas's film player writes to it.
     ==================================================================== */
  var noteEl = $('loadNote');
  var noteSpan = noteEl ? noteEl.querySelector('span') : null;
  var notes = new Map();
  function paintNote() {
    if (!noteEl || !noteSpan) return;
    var last = '';
    notes.forEach(function (v) { last = v; });
    noteSpan.textContent = last;
    noteEl.classList.toggle('is-up', !!last);
  }
  function loadNote(key, text) { notes.delete(key); notes.set(key, text); paintNote(); }
  function loadNoteDone(key) { notes.delete(key); paintNote(); }

  /* ====================================================================
     ARRIVAL
     There is no loading screen any more (Josh, 2026-09-17: "lets remove the
     loading screen since it now loads instant"). The 3D page needed one: it
     had a megabyte of engine to parse, a sheet to fetch and two texture bakes
     to run before it could draw anything. This page has an image, and the
     chart fades it in when it has decoded — which on any normal connection is
     one paint, so a splash would be a screen nobody could read.

     What survives is the one status line, for the case a splash was actually
     useful for: a sheet that is genuinely slow. It says so after 2.5 s and
     goes as soon as the chart is up. Nothing gates the page on it.
     ==================================================================== */
  var ready = false;
  var noteTimer = setTimeout(function () {
    if (!ready) loadNote('chart', 'Unfolding the chart…');
  }, 2500);

  try {
    boot();
  } catch (err) {
    clearTimeout(noteTimer);
    loadNoteDone('chart');
    if (window.console && console.error) console.error('Museum failed to start:', err);
  }

  function boot() {
    if (!D) throw new Error('museum-data.js did not load');

    /* ==================================================================
       THE CHART
       ================================================================== */
    var chartHost = $('chart');
    /* A marker opens its country; one of the country's places, once they are
       out, opens what it holds (V2.72 K24). */
    var chart = window.FORUM_CHART.mount({
      host: chartHost,
      sheets: D.sheets,
      stations: D.STATIONS.map(function (st) {
        return { uv: st.uv, label: st.name, offset: st.offset || null };
      }),
      onStation: function (i) { atlas.open(D.STATIONS[i].country); },
      onSub: function (key) { openPlace(placeOf(key)); },
      onBlank: function () {
        /* A press on bare paper drops the region, as it did — it does not
           close the panel. */
        if (atlas.region() !== null) clearRegion();
      },
      onReady: function () {
        ready = true;
        clearTimeout(noteTimer);
        loadNoteDone('chart');
        /* On a phone the sheet is wider than the screen, so the opening view
           is a region rather than the whole world — the stepper's first
           selection, framed as the chart scales in. */
        if (stepperVisible()) {
          var first = D.REGION_TAB_ORDER[0];
          chart.focus(countryCentre(first), 1, quiet ? 0 : 2600);
        }
      }
    });

    /* ---- places, markers and countries (V2.72 K24) ----------------------
       A place is a STOPS index; its sub-pin key is 'city:' + index. */
    function placeOf(key) { return Number(String(key || '').split(':')[1]); }
    function stationOf(r) { return D.STOP_STATION[r]; }
    /* What a country's markers and places cover, for framing it. */
    function countryPoints(i) {
      var pts = [];
      (D.REGIONS[i].stations || []).forEach(function (s) { pts.push(D.STATIONS[s].uv); });
      D.REGIONS[i].stops.forEach(function (r) { pts.push(D.STOPS[r].chartUv); });
      return pts;
    }
    function countryCentre(i) {
      var pts = countryPoints(i);
      var u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
      pts.forEach(function (p) {
        u0 = Math.min(u0, p[0]); u1 = Math.max(u1, p[0]);
        v0 = Math.min(v0, p[1]); v1 = Math.max(v1, p[1]);
      });
      return [(u0 + u1) / 2, (v0 + v1) / 2];
    }
    /* The strip of chart the atlas leaves in view. On a phone the sheet's
       peek stop puts its top edge at 38% of the screen (92dvh tall, lowered
       30dvh); the pills hold the top 56px either way. */
    var sheetMq = window.matchMedia ? window.matchMedia('(max-width: 860px)') : null;
    function regionFit(i) {
      var H = chartHost.clientHeight;
      var sheet = !!(sheetMq && sheetMq.matches) && atlas.isOpen();
      return {
        maxK: (D.REGION_VIEW[i] || {}).zoom || 3.364,
        top: 56,
        bottom: sheet ? Math.round(H * 0.38) : H,
        pad: sheet ? 34 : 64
      };
    }

    /* Two things light markers: the stepper, a country's worth, and a card
       under the pointer, one marker. The card wins while it is hovered, and
       the stepper's country comes back when it is not. */
    var litCountry = null, litCard = null;
    function paintLit() {
      if (litCard !== null) chart.highlightStation(litCard);
      else chart.highlightStation(litCountry === null || !D.REGIONS[litCountry] ? null : D.REGIONS[litCountry].stations);
    }

    /* The facade the atlas moves the chart through. It never sees
       FORUM_CHART, so there is one place where "frame a region" is defined. */
    var facade = {
      focusRegion: function (i, ms) {
        if (!D.REGIONS[i]) return;
        chart.fitTo(countryPoints(i), regionFit(i), ms);
      },
      home: function (ms) { chart.home(ms); },
      zoomHome: function (mult, ms) { chart.home(ms, mult); },
      setSubs: function (list) { chart.setSubs(list); },
      setRegionSheet: function (i) { chart.setRegionSheet(i); },
      /* A country's markers, lit together: the US is three now. */
      highlightStation: function (i) {
        litCountry = i === undefined ? null : i;
        paintLit();
      },
      showStations: function (mask) { chart.showStations(mask); },
      /* A card lights its place: the place's own pin if it is out on the
         chart, otherwise the marker it stands under. */
      highlightSub: function (key) {
        var out = !!key && chart.subShown(key);
        chart.highlightSub(out ? key : null);
        var st = key && !out ? stationOf(placeOf(key)) : undefined;
        litCard = st === undefined ? null : st;
        paintLit();
      },
      setPanelWidth: function (px) { chart.setPanelWidth(px); },
      setPeek: function (fraction) { chart.setPeek(fraction); }
    };

    /* ==================================================================
       THE DEAD WARRIOR SOCIETY (V2.72 K22)
       Zach's seasons, and one view per season: R3 "The War Room", Josh's
       pick off the DWS canvas. Since K24 a season is its place's entry, so
       its place's pin and its card both open it; an episode plays in the
       atlas's player, over the season, which is still there when the player
       closes.
       ================================================================== */
    var SEASONS = D.DWS_SEASONS || [];
    function seasonByKey(key) {
      for (var i = 0; i < SEASONS.length; i++) if (SEASONS[i].key === key) return SEASONS[i];
      return null;
    }
    var seasonHost = $('seasonHost');
    var seasonView = window.FORUM_SEASON || null;
    if (seasonView && seasonHost) {
      seasonView.mount({
        host: seasonHost,
        icon: D.ICON_SVG.society,
        onPlay: function (s, i) { if (atlas.playEpisode) atlas.playEpisode(s, i); }
      });
    }
    if (!seasonView && window.console) console.warn('museum-season.js did not load: a season opens straight into its first episode.');
    function openSeason(key, opener) {
      var s = typeof key === 'string' ? seasonByKey(key) : key;
      if (!s) return;
      hideTip();
      if (seasonView && seasonHost) seasonView.open(s, opener || null);
      else if (atlas.playEpisode) atlas.playEpisode(s, 0);
    }

    /* A place's pin opens what the place holds: a Society place its season,
       a journey city its Photo Cache on the wall. */
    function openPlace(r) {
      var list = (D.MEDIA[r] || []);
      if (!list.length) return;
      var season = null;
      list.forEach(function (m) { if (!season && m.season) season = m.season; });
      if (season && !D.STOPS[r].photos.length) openSeason(season, null);
      else enterStop(r, null);
    }

    /* ==================================================================
       THE PHOTO CACHES — The Hang
       ================================================================== */
    var hangHost = $('hangHost');
    var hang = window.FORUM_HANG;
    if (hang && hangHost) {
      hang.mount({
        host: hangHost,
        assets: window.MUSEUM_ASSETS || {},
        tone: hangHost.getAttribute('data-tone') || 'dark',
        onClose: leaveStop
      });
    }

    var preDive = null;
    /* The 3D page dived at a stop for 1150 ms and ended at seven times home,
       then covered the screen with a room — a long fall to a place nobody
       saw. The dive is shorter now and lands at a zoom that still reads as
       "here it is" when the wall closes and the chart comes back. */
    function enterStop(stopIndex, mediaKey) {
      var stop = D.STOPS[stopIndex];
      if (!stop || !hang) return;
      var media = null;
      if (mediaKey) {
        D.MEDIA_FLAT.forEach(function (m) { if (m.key === mediaKey) media = m; });
      }
      preDive = { zoom: chart.zoom(), region: atlas.region() };
      chart.focus(stop.chartUv, Math.min(4.2, Math.max(2.6, chart.zoom() * 1.3)), quiet ? 0 : 520);

      var open = function () {
        document.body.classList.add('hang-open');
        if (chartHost) chartHost.classList.add('is-parked');
        hang.open({
          index: stopIndex,
          title: media ? media.title : stop.name,
          kind: media ? media.kindLabel : D.MEDIA_KINDS.photo.label,
          place: D.PLACE_LABEL[stopIndex] || stop.region,
          date: media ? media.date : '',
          /* V3.0 (Josh, 2026-09-29): the place's own frames; none, the house's. */
          frames: stop.frames || '',
          photos: stop.photos
        }, null);   /* null = land on the wall: the wall is the overview */
      };
      if (quiet) open(); else setTimeout(open, 430);
    }

    function leaveStop() {
      document.body.classList.remove('hang-open');
      if (chartHost) chartHost.classList.remove('is-parked');
      /* Back where the dive started: the region's own framing if one is open,
         otherwise home. */
      if (preDive && preDive.region !== null && preDive.region !== undefined) {
        facade.focusRegion(preDive.region, quiet ? 0 : 760);
      } else {
        chart.home(quiet ? 0 : 760);
      }
      preDive = null;
    }

    /* ==================================================================
       THE FIELD ATLAS
       ================================================================== */
    /* Five files have to arrive for this page to be whole. If one of them
       does not, the rest should still work rather than taking the chart down
       with it — a missing module is a degraded page, not a black one. */
    var atlas = window.FORUM_ATLAS || {
      mount: function () {}, open: function () {}, close: function () {},
      isOpen: function () { return false; }, refresh: function () {},
      linkCity: function () {}, region: function () { return null; }
    };
    if (!window.FORUM_ATLAS && window.console) console.warn('museum-atlas.js did not load: the catalogue is unavailable.');
    if (!window.FORUM_HANG && window.console) console.warn('museum-hang.js did not load: photo caches cannot open.');
    atlas.mount({ data: D, chart: facade, onEnterStop: enterStop, onSeason: openSeason });

    function clearRegion() {
      if (atlas.clearRegion) atlas.clearRegion();
      else if (atlas.isOpen()) atlas.open(null);
      else {
        facade.setSubs([]);
        facade.highlightStation(null);
        facade.setRegionSheet(null);
        facade.showStations([]);
        chart.home(900);
      }
    }

    /* ==================================================================
       THE TOOLTIP
       Positioned off the chart's own arithmetic now (`pointScreen`) rather
       than off a projected camera, and written only when the target changes
       — the 3D page rebuilt it sixty times a second.
       ================================================================== */
    var tip = $('tooltip');
    var tipName = tip ? tip.querySelector('.t-name') : null;
    var tipRegion = tip ? tip.querySelector('.t-region') : null;
    var tipMedia = tip ? tip.querySelector('.t-media') : null;
    var tipKey = null;

    function hideTip() {
      tipKey = null;
      if (tip) tip.style.display = 'none';
    }
    function placeTip(p) {
      if (!tip || !chartHost || !p) return;
      var box = chartHost.getBoundingClientRect();
      tip.style.left = (box.left + p.x) + 'px';
      tip.style.top = (box.top + p.y) + 'px';
      tip.style.display = 'block';
    }

    /* One tip for every marker and every place (V2.72 K24), in the seasons'
       seal style (Josh: "use this style as the subheader when hovering all
       hotspots, and list in small icons which media is in that hotspot"). A
       place holding one entry is named by it, then says what it is and
       where: "Season II · Lake George, NY", "Photo Cache · San Diego, CA". A
       marker holding two places is named by its country, with the places
       under it. The tally counts entries in each kind's ink, so a season is
       one DWS and not its episodes ("a dws season is 1 dws. everything else
       is x number of whatever"). */
    function tally(entries) {
      var counts = {};
      entries.forEach(function (m) { counts[m.kind] = (counts[m.kind] || 0) + 1; });
      return Object.keys(D.MEDIA_KINDS).filter(function (k) { return counts[k]; })
        .map(function (k) {
          return '<span class="t-chip" style="color:' + D.MEDIA_KINDS[k].ink + '" title="' +
                 D.MEDIA_KINDS[k].label + '">' + D.ICON_SVG[k] + '<b>' + counts[k] + '</b></span>';
        }).join('');
    }
    function fillTip(key, stops, name) {
      if (tipKey === key) return;
      tipKey = key;
      var entries = D.MEDIA_FLAT.filter(function (m) { return stops.indexOf(m.stop) !== -1; });
      if (stops.length === 1 && entries.length === 1) {
        var m = entries[0];
        tipName.textContent = m.title;
        tipRegion.textContent = (m.season ? 'Season ' + m.season.no : D.MEDIA_KINDS[m.kind].label) +
          ' · ' + m.place;
      } else if (stops.length === 1) {
        tipName.textContent = D.STOPS[stops[0]].name;
        tipRegion.textContent = D.entriesLabel(entries.length) + ' · ' + D.PLACE_LABEL[stops[0]];
      } else {
        tipName.textContent = name;
        tipRegion.textContent = stops.map(function (r) { return D.STOPS[r].name; }).join(' · ');
      }
      if (tipMedia) tipMedia.innerHTML = tally(entries);
    }
    function showStationTip(i) {
      var st = D.STATIONS[i];
      if (!tip || !st) { hideTip(); return; }
      fillTip('st:' + i, st.stops, st.name);
      placeTip(chart.stationPoint(i));
    }
    /* A place's tip sits on its own pin when that is out, and on the marker
       it stands under when it is not. */
    function showPlaceTip(key) {
      var r = placeOf(key);
      if (!tip || !D.STOPS[r]) { hideTip(); return; }
      fillTip('city:' + r, [r]);
      placeTip(chart.subShown('city:' + r) ? chart.pointScreen(D.STOPS[r].chartUv)
                                           : chart.stationPoint(stationOf(r)));
    }

    function covered() {
      var b = document.body.classList;
      return b.contains('hang-open') || b.contains('season-open');
    }
    /* A marker lights its places' cards, and a place's pin its own. */
    chartHost.addEventListener('chart:hover', function (e) {
      if (covered()) return;
      var st = e.detail.index === null ? null : D.STATIONS[e.detail.index];
      if (!st) { hideTip(); atlas.linkCity(null); return; }
      showStationTip(e.detail.index);
      atlas.linkCity(st.stops.map(function (r) { return 'city:' + r; }));
    });
    chartHost.addEventListener('chart:subhover', function (e) {
      if (covered()) return;
      if (!e.detail.key) { hideTip(); atlas.linkCity(null); }
      else { showPlaceTip(e.detail.key); atlas.linkCity(e.detail.key); }
    });

    /* A card under the pointer lights its city on the chart and takes the
       tooltip with it — the other half of the link the panel draws. Delegated
       off the panel rather than polled: the cards carry `data-city`, so this
       needs nothing from the atlas's internals and no standing timer. */
    var cardKey = null;
    var panelEl = $('atlasPanel');
    if (panelEl) {
      panelEl.addEventListener('pointerover', function (e) {
        var card = e.target.closest ? e.target.closest('.ap-card') : null;
        var key = card ? card.dataset.city : null;
        if (key === cardKey) return;
        cardKey = key;
        facade.highlightSub(key);
        if (key) showPlaceTip(key);
        else hideTip();
      });
      panelEl.addEventListener('pointerleave', function () {
        if (!cardKey) return;
        cardKey = null;
        facade.highlightSub(null);
        hideTip();
      });
    }


    /* ==================================================================
       THE PHONE STEPPER
       The gap the survey found is closed here: the CSS shows the stepper up
       to 860 px, so this gates at 860 too. Between 641 and 860 the arrows
       used to change the label and nothing else.
       ================================================================== */
    var stepper = $('stopStepper');
    var ssName = $('ssName'), ssCount = $('ssCount'), ssOpen = $('ssOpen');
    var ssPrev = $('ssPrev'), ssNext = $('ssNext');
    var stepMq = window.matchMedia ? window.matchMedia('(max-width: 860px)') : null;
    var stepIdx = 0;
    function stepperVisible() { return !!(stepMq && stepMq.matches); }

    /* `frame` is false only at boot: the chart is still scaling in from 0.787×
       then, and framing it here would freeze it at that zoom (the 3D page got
       away with it because its settle animated the radius independently). The
       opening framing happens in the chart's ready handler instead. */
    function showStep(idx, animate, frame) {
      var order = D.REGION_TAB_ORDER;
      stepIdx = (idx + order.length) % order.length;
      var region = order[stepIdx];
      /* Both labels are written in the same breath, while they are invisible.
         The 3D page faded the name out, waited 160 ms, then wrote it — so on a
         phone you could read UNITED STATES over Italy for a sixth of a second,
         and the count underneath had already changed (Josh, 2026-09-17: "when
         u scroll to italy it still has the united states text in the
         background. that text in general lags"). Now: hide, write, flush,
         show — the fade is the same 240 ms and nothing stale is ever legible. */
      var name = D.REGIONS[region].name;
      var count = D.entriesLabel(D.MEDIA_COUNTS[region]);
      var soft = animate && !quiet;
      if (soft) {
        if (ssName) ssName.classList.add('swapping');
        if (ssCount) ssCount.classList.add('swapping');
      }
      if (ssName) ssName.textContent = name;
      if (ssCount) ssCount.textContent = count;
      if (soft) {
        /* Flush, so the labels are really at opacity 0 before the class comes
           off and the transition has somewhere to travel from. */
        if (ssName) void ssName.offsetWidth;
        if (ssName) ssName.classList.remove('swapping');
        if (ssCount) ssCount.classList.remove('swapping');
      }
      if (ssOpen) {
        ssOpen.dataset.region = String(region);
        ssOpen.setAttribute('aria-label', 'Open ' + D.REGIONS[region].name);
      }
      if (!stepperVisible()) return;
      facade.highlightStation(region);
      if (frame === false) return;
      /* A deliberate step pans; the opening selection just frames. */
      /* Pans at whatever zoom the visitor is on, including one they pinched to
         — stepping to the next region should not undo their zoom. It pans to
         the middle of the country's markers, since a country can hold several
         (K24). */
      chart.focus(countryCentre(region), chart.zoom(), animate && !quiet ? 700 : 0);
    }
    if (ssPrev) ssPrev.addEventListener('click', function () { showStep(stepIdx - 1, true); });
    if (ssNext) ssNext.addEventListener('click', function () { showStep(stepIdx + 1, true); });
    if (ssOpen) ssOpen.addEventListener('click', function () {
      atlas.open(D.REGION_TAB_ORDER[stepIdx]);
    });
    if (stepMq && stepMq.addEventListener) {
      stepMq.addEventListener('change', function () { showStep(stepIdx, false); });
    }

    /* The stepper's marker art, painted to the chart marker's own
       proportions: the outer ring dashed (the button is small enough that a
       dashed ring still reads), the inner ring at 0.762 of it, the diamond's
       tip at 0.368 and its inner diamond at half that again. Ported from the
       3D file unchanged apart from losing its tune-panel hook. */
    function mixHex(a, b, amt) {
      var pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
      function ch(sh) {
        var va = (pa >> sh) & 255, vb = (pb >> sh) & 255;
        return Math.round(va + (vb - va) * amt);
      }
      return '#' + [ch(16), ch(8), ch(0)].map(function (v) {
        return v.toString(16).padStart(2, '0');
      }).join('');
    }
    function paintStepperMarker() {
      var core = document.querySelector('#ssOpen .ss-core');
      var ring = document.querySelector('#ssOpen .ss-ring');
      if (!core || !ring) return;
      var PIN = '#f2ecdd';
      var RING_PX = 62, CORE_PX = 34, SS = 4;
      var dashR = (RING_PX / 2) * 0.87;
      var solidR = dashR * 0.762;
      var tip = dashR * 0.368;

      var rc = document.createElement('canvas');
      rc.width = rc.height = RING_PX * SS;
      var rx = rc.getContext('2d');
      var rm = rc.width / 2;
      rx.strokeStyle = 'rgba(255,255,255,1)';
      rx.lineWidth = 1.6 * SS;
      var seg = (2 * Math.PI * dashR * SS) / 52;
      rx.setLineDash([seg, seg]);
      rx.beginPath(); rx.arc(rm, rm, dashR * SS, 0, Math.PI * 2); rx.stroke();
      rx.setLineDash([]);
      rx.strokeStyle = 'rgba(255,255,255,0.45)';
      rx.lineWidth = 1.2 * SS;
      rx.beginPath(); rx.arc(rm, rm, solidR * SS, 0, Math.PI * 2); rx.stroke();

      var cc = document.createElement('canvas');
      cc.width = cc.height = CORE_PX * SS;
      var cx = cc.getContext('2d');
      var cm = cc.width / 2;
      function diamond(h) {
        cx.beginPath();
        cx.moveTo(cm, cm - h); cx.lineTo(cm + h, cm);
        cx.lineTo(cm, cm + h); cx.lineTo(cm - h, cm);
        cx.closePath();
      }
      cx.fillStyle = PIN;
      cx.strokeStyle = PIN;
      cx.lineWidth = 1.4 * SS;
      diamond(tip * 0.496 * SS); cx.fill();
      diamond(tip * SS); cx.stroke();

      core.style.backgroundImage = 'url(' + cc.toDataURL() + ')';
      var ringUrl = 'url(' + rc.toDataURL() + ')';
      ring.style.backgroundColor = mixHex(PIN, '#ffffff', 0.75);
      ring.style.webkitMaskImage = ringUrl;
      ring.style.maskImage = ringUrl;
    }
    if (stepper) {
      paintStepperMarker();
      showStep(0, false, false);
    }

    /* ==================================================================
       THE WAY IN TO THE CATALOGUE, AND THE KEYBOARD HINT
       The nav that held "Field Atlas" has been display:none since the museum
       shipped, so the only route into the panel was a marker — which always
       scoped it to one region. The pill beside the return pill is the
       unscoped way in.
       ================================================================== */
    var atlasOpen = $('atlasOpen');
    if (atlasOpen) atlasOpen.addEventListener('click', function () { atlas.open(null); });
    var navAtlas = $('navAtlas');
    if (navAtlas) navAtlas.addEventListener('click', function () { atlas.open(null); });

    /* `showKeyHint` existed in the 3D file with no caller. The chart has a
       keyboard now, so it has something to say, once. */
    var keyHint = $('keyHint');
    var hintShown = false;
    chartHost.addEventListener('chart:keypan', function () {
      if (hintShown || !keyHint) return;
      hintShown = true;
      keyHint.innerHTML = '<b>← → ↑ ↓</b> move the chart · <b>Shift</b> further';
      keyHint.classList.add('on');
      setTimeout(function () { keyHint.classList.remove('on'); }, 2600);
    });

    window.FORUM_MUSEUM = {
      mounted: true,
      loadNote: loadNote,
      loadNoteDone: loadNoteDone,
      chart: chart,
      enterStop: enterStop,
      version: '1'
    };
  }

  /* Published even if boot threw, so the atlas's film player can still speak. */
  if (!window.FORUM_MUSEUM) {
    window.FORUM_MUSEUM = { mounted: false, loadNote: loadNote, loadNoteDone: loadNoteDone, version: '1' };
  }
})();
